import { useIntl } from '@edx/frontend-platform/i18n';
import { AppContext } from '@edx/frontend-platform/react';
import { useQuery } from '@tanstack/react-query';
import { useContext } from 'react';
import { Spinner } from '@openedx/paragon';
import {
  fetchCourseEngagement,
  fetchProgressOverview,
} from '@blend-ed/blendxplatform/api';
import { getFeatureToggle } from '@blend-ed/blendxplatform/utils';
import {
  StatsCard,
  OngoingCoursesTable,
  ActivityMap,
  EmptyStateAlert,
  GetStartedCards,
  MyPointsCard,
  LeaderboardSection,
} from './components';
import useOngoingCourses from './useOngoingCourses';

import messages from './messages';

const Progress = () => {
  const intl = useIntl();
  const { authenticatedUser } = useContext(AppContext);
  const userId = authenticatedUser?.userId || authenticatedUser?.id || authenticatedUser?.user_id || null;

  const { data: progressOverview, isLoading: progressLoading } = useQuery({
    queryKey: ['progressOverview', userId],
    queryFn: () => fetchProgressOverview(userId),
    enabled: !!userId,
  });

  const { ongoingItems, isLoading: ongoingLoading } = useOngoingCourses();

  // Per-org gate for points / leaderboard. Called before the isLoading early
  // return — getFeatureToggle() runs useQuery internally, so its hook order
  // must be stable across renders.
  const featuresToggled = getFeatureToggle();
  const leaderboardEnabled = !featuresToggled.loading && !!featuresToggled.features_leaderboard;

  const endDate = new Date().toISOString().slice(0, 10);
  const startDate = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);

  const { data: courseEngagement } = useQuery({
    queryKey: ['courseEngagement', userId, startDate, endDate],
    queryFn: () => fetchCourseEngagement(userId, startDate, endDate),
    enabled: !!userId,
  });

  const coursesDone = progressOverview?.courses_completed
    ?? progressOverview?.completed_courses
    ?? progressOverview?.coursesDone
    ?? 0;
  const ongoingCourses = progressOverview?.courses_in_progress
    ?? progressOverview?.ongoing_courses
    ?? progressOverview?.coursesInProgress
    ?? ongoingItems.length;
  const totalCourses = progressOverview?.total_courses
    ?? progressOverview?.totalCourses
    ?? (coursesDone + ongoingCourses);
  const certificatesObtained = progressOverview?.certificates_obtained
    ?? progressOverview?.certificatesObtained
    ?? 0;

  const isLoading = ongoingLoading || (!!userId && progressLoading);

  const isEmpty = !isLoading
    && ongoingItems.length === 0
    && (coursesDone ?? 0) === 0
    && (certificatesObtained ?? 0) === 0;

  if (isLoading) {
    return (
      <div className="progress-page progress-page--loading">
        <Spinner animation="border" screenReaderText={intl.formatMessage(messages.loadingProgress)} />
      </div>
    );
  }

  return (
    <div className="progress-page">
      {isEmpty && <EmptyStateAlert />}

      <div className="progress-page__stats-row">
        <div className="progress-page__title-wrapper">
          <h1 className="progress-page__title">
            {intl.formatMessage(messages.learningProgress)}
          </h1>
        </div>
        <div className="progress-page__stats-card-wrapper">
          <StatsCard
            coursesDone={coursesDone}
            ongoingCourses={ongoingCourses}
            totalCourses={totalCourses}
            certificatesObtained={certificatesObtained}
            isEmpty={isEmpty}
          />
        </div>
      </div>

      <div className="progress-page__section">
        {isEmpty
          ? <GetStartedCards />
          : <OngoingCoursesTable courses={ongoingItems} />}
      </div>

      {!isEmpty && (
        <div className="progress-page__section">
          <ActivityMap engagementData={courseEngagement} />
        </div>
      )}

      {leaderboardEnabled && (
        <>
          <div className="progress-page__section">
            <MyPointsCard enabled={leaderboardEnabled} />
          </div>
          <div className="progress-page__section">
            <LeaderboardSection enabled={leaderboardEnabled} />
          </div>
        </>
      )}
    </div>
  );
};

export default Progress;
